import {useLocalStorage} from 'platform/components';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isTrue} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {sourcingStorageKey} from '../../../../../../../constants/sourcingStorageKey';
import {
  ALPHA_FILTERABLE_SUSPICIOUS_REASONS,
  isAlphaFilterableSuspiciousReason,
} from '../../../../../../../constants/sourcingSuspiciousReasons';
import {isSuspiciousReasonFilterEnabled} from '../../../../../utils/isSuspiciousReasonFilterEnabled';
import {useFilters} from '../../../../FiltersContext/FiltersContext';

export function useSuspiciousReasonSelection() {
  const {filters, onUpdateFilters} = useFilters();
  const [reasonSearch, setReasonSearch] = useState<string | null>(null);
  const [storedReasons, setStoredReasons] = useLocalStorage<unknown>(
    sourcingStorageKey.SUSPICIOUS_REASON_SELECTION,
    null
  );
  const rememberedReasons = Array.isArray(storedReasons)
    ? storedReasons.filter(
        (reason: unknown): reason is string =>
          typeof reason === 'string' && isAlphaFilterableSuspiciousReason(reason)
      )
    : null;
  const isReasonFilterEnabled = isSuspiciousReasonFilterEnabled();
  const isSuspiciousFilterActive =
    isTrue(filters.hideSuspicious) ||
    (isReasonFilterEnabled && Boolean(filters.excludeSuspiciousReasons?.length));
  const selectedReasons = match({
    hideAll: isTrue(filters.hideSuspicious),
    hasSelectedReasons: isReasonFilterEnabled && Boolean(filters.excludeSuspiciousReasons?.length),
  })
    .with({hideAll: true}, () => ALPHA_FILTERABLE_SUSPICIOUS_REASONS)
    .with({hasSelectedReasons: true}, () => filters.excludeSuspiciousReasons ?? [])
    .otherwise(() => rememberedReasons ?? ALPHA_FILTERABLE_SUSPICIOUS_REASONS)
    .filter(isAlphaFilterableSuspiciousReason);
  const reasonsByLabel = new Map<string, string[]>();
  for (const reason of ALPHA_FILTERABLE_SUSPICIOUS_REASONS) {
    const label = i18n.t(`entity.sourcing.suspiciousReason.${reason}`);
    reasonsByLabel.set(label, [...(reasonsByLabel.get(label) ?? []), reason]);
  }
  const reasonOptions = Array.from(reasonsByLabel, ([label, values]) => ({label, values})).filter(
    (option) => option.label.toLocaleLowerCase().includes(reasonSearch?.toLocaleLowerCase() ?? '')
  );

  const updateSuspiciousReasons = (reasons: string[]) => {
    const nextReasons = reasons.filter(isAlphaFilterableSuspiciousReason);
    const hideAll = nextReasons.length === ALPHA_FILTERABLE_SUSPICIOUS_REASONS.length;

    setStoredReasons(nextReasons);
    if (Boolean(filters.hideSuspicious) !== hideAll) {
      onUpdateFilters('hideSuspicious', hideAll);
    }
    onUpdateFilters(
      'excludeSuspiciousReasons',
      hideAll || nextReasons.length === 0 ? undefined : nextReasons
    );
  };

  const toggleSuspiciousFilter = (checked: boolean) =>
    match({isReasonFilterEnabled, checked})
      .with({isReasonFilterEnabled: false}, () => onUpdateFilters('hideSuspicious', checked))
      .with({checked: false}, () => {
        if (isSuspiciousFilterActive) {
          setStoredReasons(selectedReasons);
        }
        onUpdateFilters('hideSuspicious', false);
        onUpdateFilters('excludeSuspiciousReasons', undefined);
      })
      .otherwise(() =>
        updateSuspiciousReasons(
          rememberedReasons?.length ? rememberedReasons : ALPHA_FILTERABLE_SUSPICIOUS_REASONS
        )
      );

  const toggleReasons = (reasons: string[]) =>
    updateSuspiciousReasons(
      reasons.every((reason) => selectedReasons.includes(reason))
        ? selectedReasons.filter((selectedReason) => !reasons.includes(selectedReason))
        : [...new Set([...selectedReasons, ...reasons])]
    );

  return {
    isSuspiciousFilterActive,
    reasonSearch,
    setReasonSearch,
    reasonOptions,
    selectedReasons,
    toggleSuspiciousFilter,
    toggleReasons,
    selectAllReasons: () => updateSuspiciousReasons(ALPHA_FILTERABLE_SUSPICIOUS_REASONS),
    unselectAllReasons: () => updateSuspiciousReasons([]),
  };
}
